import React, { useEffect, lazy, Suspense } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Route,
    Switch,
    withRouter,
    Redirect,
    useHistory,
    useLocation,
} from 'react-router-dom';
// @ts-ignore
import { Loader } from '@els/els-react--loader';
// @ts-ignore
import { SkipLinks } from '@els/els-react--skip-links';
import {
    selectAppConfigs,
    selectAppConfigsAsyncStatus,
    selectAppRuntimeConfigsAsyncStatus,
} from './features/app-config/app-config-selector';
import { getAppRuntimeConfigs } from './features/app-config/app-config-thunk';
import Routes from './constants/routes';
import {
    sendToPendo,
    initializePendo,
    updateMetricsObject,
} from './utils/helper/pendo';
import {
    hasSucceeded,
    isComplete,
    isInitial,
    hasFailed,
} from '../libs/utils/constants/async-status';
import {
    selectUserAuthenticationStatus,
    selectAsyncStatus,
    selectOriginalRedirectUrl,
    selectCheckLoginAsyncStatus,
    selectEcrId,
    selectClientAuthorizedForSkill,
} from './features/login/login-selector';
import {
    selectClientId,
    selectIsWhitelistedIp,
    selectReferentialAccessAsyncStatus,
    selectIsIpWhitelistedAsyncStatus,
    selectClientIdByEcrIdAsyncStatus,
    selectReferentialClientAuthorizedForSkill,
} from './features/referential-access/referential-access-selector';
import { checkReferentialWhitelistThunk } from './features/referential-access/referential-access-thunk';
import { silentLoginFlow } from './features/login/login-thunk';
import GeneralHeader from './components/general-header';

const LoginCallback = lazy(() => import('./features/login/login-callback'));
const SilentLoginCallback = lazy(
    () => import('./features/login/silent-login-callback')
);
const Login = lazy(() => import('./features/login/login'));
const GeneralErrorPage = lazy(
    () => import('./components/general-error-page/general-error-page')
);
const EbpcDashboard = lazy(
    () => import('./features/evidence-based-practice-changes/ebpc-dashboard')
);
const AppConfig = lazy(() => import('./features/app-config/app-config'));

const SkillLaunch = lazy(
    () => import('./components/skill-launch/skill-launch')
);
const GeneralFooter = lazy(() => import('./components/general-footer'));
const FavoritesContent = lazy(
    () => import('./features/favorites-content/favorites-content')
);
const Search = lazy(() => import('./features/search/search'));
const Home = lazy(() => import('./features/home/home'));
const PatientEducationContent = lazy(
    () =>
        import(
            './features/search/patient-education-content/patient-education-content'
        )
);

const pendoImplementation = (pathname: string) => {
    initializePendo();
    updateMetricsObject({ name: pathname });
    sendToPendo();
};

const AppLayout = () => {
    const isUserAuthenticated = useSelector(selectUserAuthenticationStatus);
    const ecrIdByLoginApi = useSelector(selectEcrId);
    const asyncStatus = useSelector(selectAsyncStatus);
    const checkLoginAsyncStatus = useSelector(selectCheckLoginAsyncStatus);
    const appConfigs = useSelector(selectAppConfigs);
    const appConfigsAsyncStatus = useSelector(selectAppConfigsAsyncStatus);
    const appRuntimeConfigsAsyncStatus = useSelector(
        selectAppRuntimeConfigsAsyncStatus
    );
    const isErrorPage = window.location.href.includes('error-page');
    const originalRedirectUrl: any = useSelector(selectOriginalRedirectUrl);
    const redirectionUrl = [
        Routes.ROOT,
        Routes.LOGOUT,
        Routes.LOGIN,
        Routes.ERROR_PAGE,
    ].includes(originalRedirectUrl)
        ? `/${ecrIdByLoginApi}/home`
        : originalRedirectUrl;
    const dispatch = useDispatch();
    const ecrId = window.location.pathname.split('/')[1];
    // With no ECR id in the URL (e.g. launching at "/"), use the one from the token.
    const homeEcrId = ecrId || ecrIdByLoginApi;
    const homeRedirectUrl = homeEcrId
        ? `/${homeEcrId}/home`
        : Routes.ERROR_PAGE;

    const clientId = useSelector(selectClientId);
    const isReferentialAsyncStatus = useSelector(
        selectReferentialAccessAsyncStatus
    );
    const isIpWhitelistedAsyncStatus = useSelector(
        selectIsIpWhitelistedAsyncStatus
    );
    const isIpWhitelisted = useSelector(selectIsWhitelistedIp);
    const [isFooterVisible, setIsFooterVisible] = React.useState(false);
    const isReferentialAccess = React.useMemo(
        () =>
            ecrId &&
            [Routes.LOGIN_CALLBACK, Routes.ERROR_PAGE, Routes.LOGOUT].includes(
                `/${ecrId}` as '/callback' | '/logout' | '/error-page'
            ),
        [ecrId]
    );
    const getClientIdByEcrIdAsyncStatus = useSelector(
        selectClientIdByEcrIdAsyncStatus
    );
    const referentialHasSkillAccess = useSelector(
        selectReferentialClientAuthorizedForSkill
    );
    const userHasSkillAccess = useSelector(selectClientAuthorizedForSkill);
    const history = useHistory();
    const location = useLocation();

    useEffect(() => {
        if (
            ecrId &&
            !isReferentialAccess &&
            hasSucceeded(appConfigsAsyncStatus)
        ) {
            sessionStorage.setItem('ecrId', ecrId);
            dispatch(checkReferentialWhitelistThunk({ ecrId }));
        }
    }, [ecrId, isReferentialAccess, appConfigsAsyncStatus, dispatch]);

    useEffect(() => {
        if (isUserAuthenticated && isInitial(appRuntimeConfigsAsyncStatus)) {
            dispatch(getAppRuntimeConfigs());
        }
    }, [isUserAuthenticated, appRuntimeConfigsAsyncStatus, dispatch]);

    useEffect(() => {
        if (hasFailed(appRuntimeConfigsAsyncStatus)) {
            history.push(Routes.ERROR_PAGE);
        }
    }, [appRuntimeConfigsAsyncStatus, history]);

    useEffect(() => {
        const sentinel = document.getElementById('page-top-sentinel');
        if (sentinel instanceof HTMLElement) {
            sentinel.focus();
        }
    }, [location.pathname]);

    pendoImplementation(location.pathname);

    useEffect(() => {
        setIsFooterVisible(isComplete(asyncStatus) && !hasFailed(asyncStatus));
    }, [window.location.href, asyncStatus]);

    useEffect(() => {
        if (
            hasFailed(getClientIdByEcrIdAsyncStatus) ||
            (hasSucceeded(getClientIdByEcrIdAsyncStatus) &&
                !referentialHasSkillAccess)
        ) {
            history.push(Routes.ERROR_PAGE);
        }
    }, [ecrId, getClientIdByEcrIdAsyncStatus]);

    useEffect(() => {
        if (
            isUserAuthenticated &&
            !userHasSkillAccess &&
            !window.location.href.includes('/callback')
        ) {
            history.push(Routes.ERROR_PAGE);
        }
    }, [isUserAuthenticated, userHasSkillAccess]);

    useEffect(() => {
        if (clientId && !isUserAuthenticated) {
            dispatch(silentLoginFlow());
        }
    }, [clientId, isUserAuthenticated, dispatch]);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            let trustedOrigins: string[] = [];
            if (appConfigs && appConfigs.CONNECT_PLAYER_BASE_URL) {
                trustedOrigins = [
                    `${appConfigs.CONNECT_PLAYER_BASE_URL}`,
                    // 'localhost', // Uncomment this line to allow localhost
                ];
            }
            const isTrustedOrigin = trustedOrigins.some((origin) =>
                event.origin.includes(origin)
            );

            if (!isTrustedOrigin) {
                console.log('Message origin not trusted', event.origin);
                return;
            }

            if (event.data.type === 'UPDATE_URL') {
                if (
                    event.data.url !== undefined &&
                    event.data.url.split('#')[1] !== undefined
                ) {
                    const [, hash] = event.data.url.split('#');
                    if (hash && hash.includes('tab-')) {
                        window.location.hash = hash;
                    }
                }
            }
        };

        window.addEventListener('message', handleMessage, false);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [appConfigs]);

    const loaderCondition =
        !isErrorPage &&
        (!isComplete(asyncStatus) ||
            !isComplete(isReferentialAsyncStatus) ||
            !isComplete(isIpWhitelistedAsyncStatus));

    const successIPReferentialCondition =
        hasSucceeded(isReferentialAsyncStatus) &&
        hasSucceeded(isIpWhitelistedAsyncStatus);

    return (
        <>
            <span
                id="page-top-sentinel"
                tabIndex={-1}
                className="u-els-visually-hidden"
                aria-hidden="true"
            />
            <SkipLinks
                sections={[
                    {
                        id: 'content',
                        title: 'main content',
                    },
                ]}
                id="skip-link"
            />
            {!loaderCondition && <GeneralHeader />}
            <div className="app-layout">
                {loaderCondition && <Loader isVisible isBlocking />}
                <main className="app-layout__body">
                    {Object.keys(appConfigs).length === 0 && (
                        <Suspense fallback={<Loader isVisible isBlocking />}>
                            <AppConfig />
                        </Suspense>
                    )}
                    {Object.keys(appConfigs).length > 0 &&
                        hasFailed(appRuntimeConfigsAsyncStatus) && (
                            <Suspense
                                fallback={<Loader isVisible isBlocking />}
                            >
                                <GeneralErrorPage />
                            </Suspense>
                        )}
                    {Object.keys(appConfigs).length > 0 &&
                        !hasFailed(appRuntimeConfigsAsyncStatus) && (
                            // eslint-disable-next-line react/jsx-no-useless-fragment
                            <>
                                {(!isUserAuthenticated ||
                                    !userHasSkillAccess) && (
                                    <Suspense
                                        fallback={
                                            <Loader isVisible isBlocking />
                                        }
                                    >
                                        <Switch>
                                            {/* Launching without an ECR id goes straight to login; the ECR id in the URL then comes from the token. */}
                                            <Route
                                                exact
                                                path={Routes.ROOT}
                                                component={Login}
                                            />
                                            <Route
                                                exact
                                                path={[`${Routes.ERROR_PAGE}`]}
                                                component={GeneralErrorPage}
                                            />
                                            <Route
                                                exact
                                                path={Routes.LOGIN_CALLBACK}
                                                component={LoginCallback}
                                            />
                                            <Route
                                                exact
                                                path={
                                                    Routes.SILENT_LOGIN_CALLBACK
                                                }
                                                component={SilentLoginCallback}
                                            />
                                            <Route
                                                exact
                                                path={Routes.LOGOUT}
                                                component={Login}
                                            />
                                        </Switch>
                                    </Suspense>
                                )}
                                {successIPReferentialCondition &&
                                    isComplete(checkLoginAsyncStatus) &&
                                    isIpWhitelisted &&
                                    referentialHasSkillAccess &&
                                    !isUserAuthenticated && (
                                        <Suspense
                                            fallback={
                                                <Loader isVisible isBlocking />
                                            }
                                        >
                                            <Switch>
                                                <Route
                                                    exact
                                                    path={`${Routes.SKILL_LAUNCH}`}
                                                    component={SkillLaunch}
                                                />
                                                <Route
                                                    exact
                                                    path={`${Routes.HOME}`}
                                                    component={Home}
                                                />
                                                <Route
                                                    exact
                                                    path={`${Routes.SEARCH}`}
                                                    component={Search}
                                                />
                                                <Route
                                                    exact
                                                    path={[
                                                        `${Routes.EVIDENCE_BASED_PRACTICE}`,
                                                    ]}
                                                    component={EbpcDashboard}
                                                />
                                                <Route
                                                    exact
                                                    path={`${Routes.PATIENT_EDUCATION}`}
                                                    component={
                                                        PatientEducationContent
                                                    }
                                                />
                                                <Route component={Login} />
                                            </Switch>
                                        </Suspense>
                                    )}
                                {successIPReferentialCondition &&
                                    !isIpWhitelisted &&
                                    !isUserAuthenticated && (
                                        <Suspense
                                            fallback={
                                                <Loader isVisible isBlocking />
                                            }
                                        >
                                            <Switch>
                                                <Route component={Login} />
                                            </Switch>
                                        </Suspense>
                                    )}
                                {isUserAuthenticated && userHasSkillAccess && (
                                    <Suspense
                                        fallback={
                                            <Loader isVisible isBlocking />
                                        }
                                    >
                                        <Switch>
                                            <Route
                                                exact
                                                path={[
                                                    `${Routes.EVIDENCE_BASED_PRACTICE}`,
                                                ]}
                                                component={EbpcDashboard}
                                            />
                                            <Route
                                                exact
                                                path={[
                                                    `${Routes.FAVORITES_CONTENT}`,
                                                ]}
                                                component={FavoritesContent}
                                            />
                                            <Route
                                                exact
                                                path={`${Routes.SKILL_LAUNCH}`}
                                                component={SkillLaunch}
                                            />
                                            <Route
                                                exact
                                                path={`${Routes.SEARCH}`}
                                                component={Search}
                                            />
                                            <Route
                                                exact
                                                path={`${Routes.HOME}`}
                                                component={Home}
                                            />
                                            <Route
                                                exact
                                                path={`${Routes.PATIENT_EDUCATION}`}
                                                component={
                                                    PatientEducationContent
                                                }
                                            />
                                            <Redirect
                                                from={`${Routes.LOGIN_CALLBACK}*`}
                                                to={redirectionUrl}
                                            />
                                            <Redirect
                                                from={`${Routes.ROOT}*`}
                                                to={homeRedirectUrl}
                                            />
                                            <Redirect
                                                from="*"
                                                to={Routes.ROOT}
                                            />
                                        </Switch>
                                    </Suspense>
                                )}
                            </>
                        )}
                </main>
                {isFooterVisible && (
                    <Suspense fallback={<Loader isVisible isBlocking />}>
                        <GeneralFooter />
                    </Suspense>
                )}
            </div>
        </>
    );
};

export default withRouter(AppLayout);
